import { Getter, Mutation, State } from 'vuex-simple';
import { sagaDispatchResolve } from 'vuex-coolstory';

import { getCRBActionType, getFRBActionType } from './sagas/helpers';
import NotificationsModule, { NotyType } from './NotificationsModule';

import { getT } from '../translate';

const $t = getT();

interface IPayloadRequestSuccess<TData> {
	data: TData | null;
	options: RM.IOptionsBaseRest;
	lastRequest: TRA.IResponse['request'];
}

interface IPayloadRequestError {
	requestError: TRA.IResponse;
	options: RM.IOptionsBaseRest;
}

const optionsByDefGlobal = {
	useGlobalNoty: true,
	throwingError: true,
	isWaitingBackend: false
};

export default abstract class BaseRestModule<TData> {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	protected abstract service: any;
	protected abstract notifications: NotificationsModule;
	protected abstract execute(options?: RM.IOptionsExecute): Promise<TData | TRA.IResponse<TData>>;
	protected abstract optionsByDef: object;
	protected abstract appName: string;

	@State()
	public data: TData | null = null;

	@State()
	public lastRequest: TRA.IResponse['request'] | null = null;

	@State()
	public datas: Common.IMap<TData | null> = {};

	@State()
	public loading: boolean = false;

	@State()
	public loadingMap: Common.IMap<boolean> = {};

	@State()
	public error!: TRA.IResponse;

	protected getOptionsByDef = (options?: Partial<RM.IOptionsBaseRest>): RM.IOptionsBaseRest => {
		const optionsByDef = { ...optionsByDefGlobal, ...(this.optionsByDef || {}) };
		const opts = options || optionsByDef;

		if (opts.useGlobalNoty === undefined) {
			opts.useGlobalNoty = optionsByDef.useGlobalNoty;
		}

		if (opts.throwingError === undefined) {
			opts.throwingError = optionsByDef.throwingError;
		}

		if (opts.isWaitingBackend === undefined) {
			opts.isWaitingBackend = optionsByDef.isWaitingBackend;
		}

		return opts as RM.IOptionsBaseRest;
	};

	protected async doRawRequest({ request, options }: RM.IRequest<TData>): Promise<TRA.IResponse<TData>> {
		const opts = this.getOptionsByDef(options);
		this.requestStart(opts);

		try {
			const response: TRA.IResponse<TData> = (await request()) || {};
			const { request: lastRequest } = response;
			this.requestSuccess({ data: response.data, options: opts, lastRequest });
			return response;
		} catch (err) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const e: any = err;
			const error: TRA.IResponse<RM.IRequestError> = e.response || e;

			this.requestError({ requestError: error, options: opts });
			this.addNoty(error, opts);

			if (opts.throwingError) {
				throw e;
			} else {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				return e as any;
			}
		} finally {
			this.requestFinish(opts);
		}
	}

	protected async doRequest({ request, options }: RM.IRequest<TData>): Promise<TData> {
		const opts = this.getOptionsByDef(options);

		// Optimistic update set data
		opts.OU && opts.OU.positiv();

		this.requestStart(opts);

		if (opts.isWaitingBackend) {
			await sagaDispatchResolve({
				type: getCRBActionType(this.appName),
				resolver: getFRBActionType(this.appName)
			});
		}

		try {
			const response: TRA.IResponse<TData> = (await request()) || {};
			const { request: lastRequest } = response;
			let { data } = response;

			data = opts.transform ? opts.transform(data) : data;

			this.requestSuccess({ data, options: opts, lastRequest });
			return data;
		} catch (err) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const e: any = err;
			const error: TRA.IResponse<RM.IRequestError> = e.response || e;

			// Optimistic update rollback data
			opts.OU && opts.OU.negativ();

			this.requestError({ requestError: error, options: opts });
			this.addNoty(error, opts);

			if (opts.throwingError) {
				throw e;
			} else {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				return e as any;
			}
		} finally {
			this.requestFinish(opts);
		}
	}

	@Mutation()
	protected requestSuccess(payload: IPayloadRequestSuccess<TData>): void {
		const { data, options, lastRequest } = payload;

		this.data = data;

		if (options.usedId) {
			this.datas = {
				...this.datas,
				[options.usedId]: data
			};
		}

		this.lastRequest = lastRequest;
	}

	@Mutation()
	protected requestStart(options: RM.IOptionsBaseRest): void {
		this.loading = true;

		if (options.usedId) {
			this.loadingMap = {
				...this.loadingMap,
				[options.usedId]: true
			};
		}
	}

	@Mutation()
	protected requestFinish(options: RM.IOptionsBaseRest): void {
		this.loading = false;

		if (options.usedId) {
			this.loadingMap = {
				...this.loadingMap,
				[options.usedId]: false
			};
		}
	}

	@Mutation()
	protected requestError(payload: IPayloadRequestError): void {
		this.error = payload.requestError;
	}

	@Getter()
	public get loaded(): boolean {
		return !!this.data;
	}

	@Getter()
	public get loadedMap(): Common.IMap<boolean> {
		return Object.keys(this.datas).reduce((acc, curr) => ({ ...acc, [curr]: !!this.datas[curr] }), {});
	}

	private addNoty(error: TRA.IResponse<RM.IRequestError>, options: RM.IOptionsBaseRest): void {
		const message = this.getMessageError(error.data, options);
		const type = NotyType.error;
		const title = '';
		// @ts-ignore
		const correlationId = error.request?.response?.headers['X-Correlation-ID'] || null;

		options.useGlobalNoty && this.notifications.add({ type, message, title, correlationId, escapeHTML: false });
	}

	// Need rewrite for backend format
	private getMessageErrorBackend(requestError: RM.IRequestError): string {
		const { error, message } = requestError || {};
		const hasError = Boolean(error && message);

		return hasError ? message : $t('Something went wrong, please, contact support');
	}

	private getMessageError(requestError: RM.IRequestError, options: RM.IOptionsBaseRest): string {
		const messageErrorFromOptions = options.getMessageError && options.getMessageError(requestError);

		return messageErrorFromOptions || this.getMessageErrorBackend(requestError);
	}
}
